import { FC, PropsWithChildren } from 'react';

import classNames from 'classnames';

import dataAttributes from '../../../../utils/attributes/attributes';
import { Icon } from '../../../icon/components/Icon/Icon';
import { Typography } from '../../../typography/components/Typography/Typography';

interface Props extends PropsWithChildren {
   variant?: 'primary' | 'inverse-primary' | 'secondary' | 'disabled' | 'success' | 'error' | 'warning' | 'info' | 'neutral' | 'eco';
   weight?: 'regular' | 'bold';
   size?: 'regular' | 'extra-large';
   leadingIcon?: string;
   trailingIcon?: string;
   attributes?: Record<string, string>;
}

const variantClassNames = {
   primary: 'bg-component-data-display-badge-container-color-background-primary text-component-data-display-badge-label-color-text-on-primary',
   'inverse-primary': 'bg-component-data-display-badge-container-color-background-inverse-primary text-component-data-display-badge-label-color-text-on-inverse-primary',
   secondary: 'bg-component-data-display-badge-container-color-background-inverse-primary text-component-data-display-badge-label-color-text-on-inverse-primary',
   disabled: 'bg-component-data-display-badge-container-color-background-disabled text-component-data-display-badge-label-color-text-on-disabled',
   success: 'bg-component-data-display-badge-container-color-background-success text-component-data-display-badge-label-color-text-on-success',
   error: 'bg-component-data-display-badge-container-color-background-danger text-component-data-display-badge-label-color-text-on-danger',
   warning: 'bg-component-data-display-badge-container-color-background-warning text-component-data-display-badge-label-color-text-on-warning',
   info: 'bg-component-data-display-badge-container-color-background-info text-component-data-display-badge-label-color-text-on-info',
   neutral: 'bg-component-data-display-badge-container-color-background-neutral text-component-data-display-badge-label-color-text-on-neutral',
   eco: 'bg-component-data-display-badge-container-color-background-eco text-component-data-display-badge-label-color-text-on-eco'
};

export const Badge: FC<Props> = ({ variant = 'primary', leadingIcon, trailingIcon, weight = 'bold', size = 'regular', children, attributes }) => (
   <div
      data-testid="badge-container"
      {...dataAttributes(attributes)}
      className={classNames('w-fit rounded-component-data-display-badge-container-border-radius min-w-component-data-display-badge-container-sizing-min-width text-center content-center box-border flex items-center whitespace-nowrap', variantClassNames[variant], {
         'pl-component-data-display-badge-container-spacing-padding-x-no-icon-regular': size === 'regular' && !leadingIcon,
         'pl-component-data-display-badge-container-spacing-padding-x-icon-regular': size === 'regular' && leadingIcon,
         'pr-component-data-display-badge-container-spacing-padding-x-no-icon-regular': size === 'regular' && !trailingIcon,
         'pr-component-data-display-badge-container-spacing-padding-x-icon-regular': size === 'regular' && trailingIcon,
         'pl-component-data-display-badge-container-spacing-padding-x-no-icon-xl': size === 'extra-large' && !leadingIcon,
         'pl-component-data-display-badge-container-spacing-padding-x-icon-xl': size === 'extra-large' && leadingIcon,
         'pr-component-data-display-badge-container-spacing-padding-x-no-icon-xl': size === 'extra-large' && !trailingIcon,
         'pr-component-data-display-badge-container-spacing-padding-x-icon-xl': size === 'extra-large' && trailingIcon,
         'py-component-data-display-badge-container-spacing-padding-y-regular gap-component-data-display-badge-container-spacing-gap-regular': size === 'regular',
         'py-component-data-display-badge-container-spacing-padding-y-xl gap-component-data-display-badge-container-spacing-gap-xl': size === 'extra-large'
      })}
   >
      {leadingIcon && (
         <div data-testid="badge-leading-icon">
            <Icon icon={leadingIcon} size="small" />
         </div>
      )}
      {children && (
         <Typography color="inherit" variant={weight === 'regular' ? 'small' : 'small-highlight'}>
            {children}
         </Typography>
      )}
      {trailingIcon && (
         <div data-testid="badge-trailing-icon">
            <Icon icon={trailingIcon} size="small" />
         </div>
      )}
   </div>
);
